/**
 * Capture a JPEG screenshot of the current document body.
 *
 * Uses html2canvas — pure client-side DOM rasterization, no native browser
 * permission prompts, no third-party services. The output is downscaled to
 * a target max width (default 1920px) and JPEG-compressed at quality 0.7
 * so the resulting data URL stays well under the backend's 6 MB cap.
 *
 * Returns a base64 data URL (``data:image/jpeg;base64,...``) on success,
 * or ``null`` if capture fails for any reason — callers should treat null
 * as "no screenshot" rather than aborting the user's flow.
 */
import html2canvas from 'html2canvas';

export interface CaptureOptions {
  /** Element to capture. Defaults to document.body. */
  target?: HTMLElement;
  /** Cap the captured width in CSS pixels. Wider sources are scaled down. */
  maxWidth?: number;
  /** JPEG quality, 0-1. Lower = smaller file, more compression artifacts. */
  jpegQuality?: number;
}

const DEFAULTS: Required<Omit<CaptureOptions, 'target'>> = {
  maxWidth: 1920,
  jpegQuality: 0.7,
};

export async function captureScreenshot(
  options: CaptureOptions = {},
): Promise<string | null> {
  const target = options.target ?? document.body;
  const { maxWidth, jpegQuality } = { ...DEFAULTS, ...options };

  // html2canvas's ``scale`` controls the device-pixel multiplier. On
  // a retina display the default is 2, which produces unnecessarily
  // large captures. We compute the smallest scale that keeps the
  // output within ``maxWidth`` to avoid 6+ MB payloads on big monitors.
  const sourceWidth = target.offsetWidth || window.innerWidth;
  const scale = sourceWidth > maxWidth ? maxWidth / sourceWidth : 1;

  try {
    const canvas = await html2canvas(target, {
      scale,
      useCORS: true,
      logging: false,
      // Skip elements explicitly marked private — gives users an escape
      // hatch to scrub PHI before submitting.
      ignoreElements: (el) => el.hasAttribute('data-feedback-skip'),
      // Backgrounds: keep the page's actual bg so the screenshot
      // looks like what the user saw, not a transparent void.
      backgroundColor: null,
    });
    return canvas.toDataURL('image/jpeg', jpegQuality);
  } catch (err) {
    // html2canvas can throw on cross-origin canvas taint, font-loading
    // races, or memory pressure. Treat any failure as "no screenshot"
    // — the feedback submission should still succeed without one.
    // eslint-disable-next-line no-console
    console.warn('[feedback] screenshot capture failed:', err);
    return null;
  }
}
